/**
 * Vendor Intelligence — customer-facing investigation status vocabulary.
 * PRESENTATION ONLY.
 *
 * Exactly four states may ever be shown to a customer inside the Vendor
 * Intelligence chapter. No internal enum, provider status, confidence score,
 * or calculation is changed here — this module only decides which of the four
 * words is printed and in which colour.
 *
 *   verified           Confirmed using connected data sources.            (green)
 *   no_evidence        Investigation completed; nothing concerning found.  (gray)
 *   awaiting_provider  Required external provider is not connected.       (yellow)
 *   informational      Background context only; neither good nor bad.     (blue)
 */

import { CheckCircle2, Circle, Clock, Info } from "lucide-react";
import type React from "react";

export type InvestigationState =
  | "verified"
  | "no_evidence"
  | "awaiting_provider"
  | "informational";

interface StateCopy {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  badge: string;
  icon: string;
  /** One-line meaning, used where a short caption is required. */
  meaning: string;
}

const STATE: Record<InvestigationState, StateCopy> = {
  verified: {
    label: "Verified",
    Icon: CheckCircle2,
    badge: "border-emerald-600/30 bg-emerald-50 text-emerald-700",
    icon: "text-emerald-700",
    meaning: "Confirmed using connected data sources.",
  },
  no_evidence: {
    label: "No Evidence Found",
    Icon: Circle,
    badge: "border-border/70 bg-muted/60 text-muted-foreground",
    icon: "text-muted-foreground",
    meaning: "This investigation completed and found nothing concerning.",
  },
  awaiting_provider: {
    label: "Awaiting Provider",
    Icon: Clock,
    badge: "border-amber-500/40 bg-amber-50 text-amber-800",
    icon: "text-amber-600",
    meaning:
      "The external provider required for this check is not currently connected.",
  },
  informational: {
    label: "Informational",
    Icon: Info,
    badge: "border-trust/30 bg-trust-soft text-trust",
    icon: "text-trust",
    meaning: "Background information only — neither positive nor negative.",
  },
};

export function investigationStatusLabel(state: InvestigationState): string {
  return STATE[state].label;
}

export function investigationStatusMeaning(state: InvestigationState): string {
  return STATE[state].meaning;
}

export function InvestigationStatusIcon({
  state,
  className = "h-4 w-4 shrink-0",
}: {
  state: InvestigationState;
  className?: string;
}) {
  const s = STATE[state];
  return <s.Icon className={`${className} ${s.icon}`} aria-hidden />;
}

export function InvestigationStatusBadge({
  state,
  className = "",
}: {
  state: InvestigationState;
  className?: string;
}) {
  const s = STATE[state];
  return (
    <span
      data-investigation-state={state}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${s.badge} ${className}`}
    >
      <s.Icon className="h-3 w-3" aria-hidden />
      {s.label}
    </span>
  );
}

/** The one-time chapter notice that explains the Awaiting Provider state. */
export function InvestigationCoverageNotice({
  className = "",
}: {
  className?: string;
}) {
  return (
    <div
      data-pdf-include="true"
      data-coverage-notice="true"
      className={`print-avoid-break rounded-2xl border border-amber-500/30 bg-amber-50/60 p-5 ${className}`}
    >
      <div className="flex items-start gap-3">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy">
            Investigation Coverage Notice
          </div>
          <p className="mt-1.5 text-sm leading-6 text-foreground/85">
            Some investigations require external providers that are not yet
            connected. Investigations marked{" "}
            <strong className="font-semibold text-amber-800">
              Awaiting Provider
            </strong>{" "}
            were not completed because ClaimGuard could not access the required
            source. This is not treated as adverse evidence and does not
            negatively affect the vendor.
          </p>
        </div>
      </div>
    </div>
  );
}
